import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Button,
  Card,
  CardActionArea,
  CardHeader,
  CardContent,
  Box,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Table,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Snackbar,
  Alert,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from '@mui/icons-material/Add';
import WorkIcon from "@mui/icons-material/Work";
import BaseWrapper from "../../components/shared/BaseWrapper";
import { getTechnicians } from "../API/technicianAPI";
import { putJobOrder } from "../API/jobOrderAPI";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

export default function BatchAssign() {
  const navigate = useNavigate();

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [groupedJobOrders, setGroupedJobOrders] = useState([]);
  const [assignedGroups, setAssignedGroups] = useState([]);
  const [extraGroups, setExtraGroups] = useState([]);
  const [unassignedOrders, setUnassignedOrders] = useState([]);
  const [technicians, setTechnicians] = useState([]);

  // for editing existing assigned groups
  const [editGroupOpen, setEditGroupOpen] = useState(false);
  const [editGroupIndex, setEditGroupIndex] = useState(null);
  const [editSelectedTechIds, setEditSelectedTechIds] = useState([]);
  // for editing extra groups
  const [editExtraGroupOpen, setEditExtraGroupOpen] = useState(false);
  const [editExtraGroupIndex, setEditExtraGroupIndex] = useState(null);
  const [editExtraSelectedTechIds, setEditExtraSelectedTechIds] = useState([]);
  // for saving data to the database
  const [dirtyExtra, setDirtyExtra]   = useState({}); 
  const [savedExtra, setSavedExtra]   = useState({});
  const [dirtyAssigned, setDirtyAssigned] = useState({});
  const [savedAssigned, setSavedAssigned] = useState({});
  const [dirtyUnassigned, setDirtyUnassigned] = useState({});
  const [savedUnassigned, setSavedUnassigned] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // detect any unsaved cards
  const hasUnsavedAssignments = 
    Object.values(dirtyAssigned).some(v => v) ||
    Object.values(dirtyExtra).some(v => v) ||
    Object.values(dirtyUnassigned).some(v => v);


  useEffect(() => {
    // 1) Try loading from the new split-out keys first
   const assignedStore   = sessionStorage.getItem("assignedGroups");
   const unassignedStore = sessionStorage.getItem("unassignedGroup");
   console.log(unassignedStore)
   if (assignedStore || unassignedStore) {
     try {
       const assignedData = assignedStore
         ? JSON.parse(assignedStore)
         : [];
       setAssignedGroups(
         assignedData.map(g => ({
           ...g,
           orders: [...g.orders].sort((a,b) => a.start_time.localeCompare(b.start_time))
         }))
       );

       const unassignedData = unassignedStore
         ? JSON.parse(unassignedStore)
         : { orders: [] };
       setUnassignedOrders(
         (unassignedData.orders || [])
           .sort((a,b) => a.start_time.localeCompare(b.start_time))
       );
     } catch (e) {
       console.error("Failed to parse split session keys:", e);
     }
   }

   // 2) Your existing logic stays exactly as is:
    const stored = sessionStorage.getItem("groupedJobOrders");
    if (stored) {
      try {
        const data = JSON.parse(stored);
        setGroupedJobOrders(data);
        const assigned = data
          .filter(g => g.technicians[0]?.id !== "unassigned")
          .map(g => ({ ...g, orders: [...g.orders].sort((a, b) => a.start_time.localeCompare(b.start_time)) }));
        setAssignedGroups(assigned);
        const unassigned = data
          .filter(g => g.technicians[0]?.id === "unassigned")
          .flatMap(g => g.orders)
          .sort((a, b) => a.start_time.localeCompare(b.start_time));
        setUnassignedOrders(unassigned);
      } catch (e) {
        console.error(e);
      }
    }
    getTechnicians().then(data => setTechnicians(data || []));
  }, []);

  const formatTime = timeStr => {
    const [h, m] = timeStr.split(":");
    const d = new Date();
    d.setHours(+h, +m);
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  // Group unassigned by city for UI
  const cityGroups = useMemo(() => {
    return unassignedOrders.reduce((acc, order) => {
      // adjust this path if your city lives elsewhere on the order object
      const city = order.client?.address?.city || order.city;
      if (!acc[city]) acc[city] = [];
      acc[city].push(order);
      return acc;
    }, {});
  }, [unassignedOrders]);

  const sortedCities = useMemo(() => {
    return Object
      .keys(cityGroups)                       // all cities present
      .filter(city => cityGroups[city].length) // only those with ≥1 order
      .sort((a, b) => a.localeCompare(b));     // alphabetically
  }, [cityGroups]);

  // const isUnassigned = id => id === 'unassigned' || id.startsWith('unassigned-');

  const handleDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
  
    const srcId = source.droppableId;
    const destId = destination.droppableId;
  
    // 1) Block cross-city unassigned drops
    if (
      srcId.startsWith("unassigned-") &&
      destId.startsWith("unassigned-") &&
      srcId !== destId
    ) return;
  
    // 2) Reorder *within* the same container
    if (srcId === destId) {
      // — Assigned groups —
      if (srcId.startsWith("assigned-")) {
        const idx = +srcId.split("-")[1];
        const newGroups = assignedGroups.map(g => ({ ...g, orders: [...g.orders] }));
        const [moved] = newGroups[idx].orders.splice(source.index, 1);
        newGroups[idx].orders.splice(destination.index, 0, moved);
        setAssignedGroups(newGroups);
  
      // — Extra groups —
      } else if (srcId.startsWith("extra-")) {
        const idx = +srcId.split("-")[1];
        const newGroups = extraGroups.map(g => ({ ...g, orders: [...g.orders] }));
        const [moved] = newGroups[idx].orders.splice(source.index, 1);
        newGroups[idx].orders.splice(destination.index, 0, moved);
        setExtraGroups(newGroups);
      }
      return;
    }
  
    // 3a) Moving *between* two assignedGroups
    if (srcId.startsWith("assigned-") && destId.startsWith("assigned-")) {
      const from = +srcId.split("-")[1];
      const to   = +destId.split("-")[1];
      const newGroups = assignedGroups.map(g => ({ ...g, orders: [...g.orders] }));
      const [moved] = newGroups[from].orders.splice(source.index, 1);
      newGroups[to].orders.splice(destination.index, 0, moved);
      setAssignedGroups(newGroups);
      // flag this assigned‐group as “unsaved”
      setDirtyAssigned(d => ({
        ...d,
        [from]: true,
        [to]:   true
      }));
      return;
    }
  
    // 3b) Moving *between* two extraGroups
    if (srcId.startsWith("extra-") && destId.startsWith("extra-")) {
      const from = +srcId.split("-")[1];
      const to   = +destId.split("-")[1];
      const newGroups = extraGroups.map(g => ({ ...g, orders: [...g.orders] }));
      const [moved] = newGroups[from].orders.splice(source.index, 1);
      newGroups[to].orders.splice(destination.index, 0, moved);
      setExtraGroups(newGroups);
      // flag this extra‐group as “unsaved”
      setDirtyExtra(d => ({
        ...d,
        [from]: true,
        [to]:   true
      }));
      // if source is now empty, clear its dirty flag
      if (newGroups[from].orders.length === 0) {
        setDirtyExtra(d => { const c = { ...d }; delete c[srcId]; return c; });
      }
      return;
    }
  
    // 3c) Cross-type moves (assigned ↔ extra, assigned/extra ↔ unassigned)
    let movedOrder;
    if (srcId.startsWith("assigned-")) {
      const idx = +srcId.split("-")[1];
      const newGroups = assignedGroups.map(g => ({ ...g, orders: [...g.orders] }));
      movedOrder = newGroups[idx].orders.splice(source.index, 1)[0];
      setAssignedGroups(newGroups);
      // flag this assigned‐group as “unsaved”
      setDirtyAssigned(d => ({ ...d, [idx]: true }));
  
    } else if (srcId.startsWith("extra-")) {
      const idx = +srcId.split("-")[1];
      const newGroups = extraGroups.map(g => ({ ...g, orders: [...g.orders] }));
      movedOrder = newGroups[idx].orders.splice(source.index, 1)[0];
      setExtraGroups(newGroups);
      // if that group is now empty, clear its dirty flag
      if (newGroups[idx].orders.length === 0) {
        setDirtyExtra(d => { const c = { ...d }; delete c[srcId]; return c; });
      }
  
    } else if (srcId.startsWith("unassigned-")) {
      movedOrder = unassignedOrders.find(o => o.id === draggableId);
      setUnassignedOrders(prev => prev.filter(o => o.id !== draggableId));
      // setDirtyUnassigned(d => ({ ...d, [srcId]: true }));
    }
  
    // — Add into destination —
    if (destId.startsWith("assigned-")) {
      const idx = +destId.split("-")[1];
      const newGroups = assignedGroups.map(g => ({ ...g, orders: [...g.orders] }));
      newGroups[idx].orders.splice(destination.index, 0, movedOrder);
      setAssignedGroups(newGroups);
      // flag this assigned‐group as “unsaved”
      setDirtyAssigned(d => ({ ...d, [idx]: true }));
  
    } else if (destId.startsWith("extra-")) {
      const idx = +destId.split("-")[1];
      const newGroups = extraGroups.map(g => ({ ...g, orders: [...g.orders] }));
      newGroups[idx].orders.splice(destination.index, 0, movedOrder);
      setExtraGroups(newGroups);
      // flag this extra‐group as “unsaved”
      setDirtyExtra(d => ({ ...d, [idx]: true }));
  
    } else if (
      destId === 'unassigned-placeholder' ||
      destId.startsWith('unassigned-')
    ) {
      const updated = [...unassignedOrders];
      updated.splice(destination.index, 0, movedOrder);
      setUnassignedOrders(updated);      
      
      // compute the real city key from the order itself:
      const cityName = movedOrder.client?.address?.city || movedOrder.city;
      const key      = `unassigned-${cityName.replace(/\s+/g, '_')}`;

      // flag this unassigned‐group as “unsaved”
      setDirtyUnassigned(d => ({ ...d, [key]: true }));
    }
  };  

  // Track used technicians
  const usedTechIds = new Set();
  assignedGroups.forEach(g => g.technicians.forEach(t => usedTechIds.add(t.id)));
  extraGroups.forEach(g => g.technicians.forEach(t => usedTechIds.add(t.id)));

  // Create a new TEMPORARY GROUP immediately
  const handleOpenNewGroup = () => {
    // figure out existing TEMPORARY GROUP numbers in both assigned and extra groups
    const existingNames = new Set();
    extraGroups.forEach(g => {
      if (typeof g.name === 'string' && g.name.startsWith('TEMPORARY GROUP ')) {
        existingNames.add(g.name);
      }
    });
    assignedGroups.forEach(g => {
      // if this group was saved as a placeholder, its name may be in technicians[0].id
      if (g.name && typeof g.name === 'string' && g.name.startsWith('TEMPORARY GROUP ')) {
        existingNames.add(g.name);
      } else if (Array.isArray(g.technicians) && g.technicians.length === 1) {
        const tech = g.technicians[0];
        if (tech.id.startsWith('TEMPORARY GROUP ')) {
          existingNames.add(tech.id);
        }
      }
    });

    // parse out numbers
    const nums = Array.from(existingNames)
      .map(n => parseInt(n.replace('TEMPORARY GROUP ', ''), 10))
      .filter(n => !isNaN(n));
    const next = nums.length ? Math.max(...nums) + 1 : 1;
    const name = `TEMPORARY GROUP ${next}`;

    // append new placeholder group
    setExtraGroups(prev => [...prev, { name, technicians: [], orders: [] }]);
  };

  const handleRemoveGroup = (isAssigned, index) => {
    if (isAssigned) {
      const group = assignedGroups[index];
      // remove the assigned-group card
      setAssignedGroups(prev => prev.filter((_, i) => i !== index));
      // push all its orders back into unassigned
      setUnassignedOrders(prev =>
        [...prev, ...group.orders].sort((a, b) => a.start_time.localeCompare(b.start_time))
      );
      // mark each affected city’s unassigned card dirty so its Save button shows
      group.orders.forEach(o => {
        const city = o.client?.address?.city || o.city;
        const key  = `unassigned-${city.replace(/\s+/g, '_')}`;
        setDirtyUnassigned(d => ({ ...d, [key]: true }));
      });
    } else {
      // grab the extra‐group we’re about to delete
     const group = extraGroups[index];
     // remove that extra group card
      setExtraGroups(prev => prev.filter((_, i) => i !== index));
     // merge its orders back into unassigned
     setUnassignedOrders(prev =>
       [...prev, ...group.orders].sort((a, b) =>
         a.start_time.localeCompare(b.start_time)
       )
     );
    }
  };

  // handlers for editing technicians inside assignedGroups
  const handleOpenEditGroup = idx => {
    const currentIds = assignedGroups[idx].technicians.map(t => t.id);
    setEditGroupIndex(idx);
    setEditSelectedTechIds(currentIds);
    setEditGroupOpen(true);
  };
  const handleCloseEditGroup = () => setEditGroupOpen(false);
  
  const handleEditCheckboxChange = (groupIndex, techId) => e => {
     const checked = e.target.checked;
     setEditSelectedTechIds(prev =>
       checked
         ? [...prev, techId]
         : prev.filter(id => id !== techId)
     );
    // mark this assignedGroup “dirty” so its Save button shows
    setDirtyAssigned(d => ({ ...d, [groupIndex]: true }));
  };

  const handleSaveEditGroup = () => {
    setAssignedGroups(prev =>
      prev.map((g, i) =>
        i === editGroupIndex
          ? {
              ...g,
              technicians: technicians.filter(t =>
                editSelectedTechIds.includes(t.id)
              )
            }
          : g
      )
    );
    handleCloseEditGroup();
  };

  // handlers for editing technicians inside extraGroups
  const handleOpenEditExtraGroup = idx => {
    const currentIds = extraGroups[idx].technicians.map(t => t.id);
    setEditExtraGroupIndex(idx);
    setEditExtraSelectedTechIds(currentIds);
    setEditExtraGroupOpen(true);
  };
  const handleCloseEditExtraGroup = () => setEditExtraGroupOpen(false);

  // Checkbox toggle for extraGroup edit
  const handleExtraCheckboxChange = techId => e => {
    setEditExtraSelectedTechIds(prev =>
      e.target.checked
        ? [...prev, techId]
        : prev.filter(id => id !== techId)
    );
  };

  // Save edits back into extraGroups
  const handleSaveEditExtraGroup = () => {
    setExtraGroups(prev =>
      prev.map((g, i) =>
        i === editExtraGroupIndex
          ? {
              ...g,
              technicians: technicians.filter(t =>
                editExtraSelectedTechIds.includes(t.id)
              )
            }
          : g
      )
    );
    handleCloseEditExtraGroup();
  };

  // Snackbar
  const handleSnackbarClose = (_event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };


  async function handleSaveExtra(idx) {
    const group = extraGroups[idx];
    // Only send firstname, id, and lastname
    // const techs = group.technicians.map(({ firstname, id, lastname }) => ({
    //   firstname,
    //   id,
    //   lastname
    // }));

    // Build the list of technicians; if none selected, use the placeholder name
    let techs = group.technicians.map(({ firstname, id, lastname }) => ({
      firstname,
      id,
      lastname
    }));
    if (techs.length === 0 && group.name.startsWith("TEMPORARY GROUP ")) {
      techs = [{ firstname: group.name, id: group.name, lastname: "" }];
    }
  
    try {
      // update each order in parallel
      await Promise.all(
        group.orders.map(order =>
          putJobOrder(order.id, { technicians: techs })
        )
      );
      // on success mark saved, clear dirty
      setSavedExtra(s => ({ ...s, [idx]: true }));
      setDirtyExtra(d => ({ ...d, [idx]: false }));
      // show success message
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Failed to save extra group:", err);
      // you can surface an error toast here
    }
  }
  

  async function handleSaveAssigned(idx) {
    const group = assignedGroups[idx];
    const techs = group.technicians.map(({ firstname, id, lastname }) => ({
      firstname, id, lastname
    }));
  
    try {
      await Promise.all(
        group.orders.map(order =>
          putJobOrder(order.id, { technicians: techs })
        )
      );
      setSavedAssigned(s => ({ ...s, [idx]: true }));
      setDirtyAssigned(d => ({ ...d, [idx]: false }));
      setSnackbarOpen(true);  // reuse your existing snackbar
    } catch (err) {
      console.error("Failed to save assigned group:", err);
    }
  }  


  async function handleSaveUnassigned(cityKey) {
    // pull the orders currently in that city‐bucket
    const orders = cityGroups[cityKey];
    try {
      // clear technicians on each order
      await Promise.all(
        orders.map(o => putJobOrder(o.id, { technicians: [] }))
      );
      // clear the dirty flag
      const key = `unassigned-${cityKey.replace(/\s+/g,'_')}`;
      setSavedUnassigned(s => ({ ...s, [key]: true }));
      setDirtyUnassigned(d => ({ ...d, [key]: false }));
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Failed to save unassigned:", err);
    }
  }



  return (
    <BaseWrapper
      titleIcon={<WorkIcon />}
      title="Operations"
      subTitle="Batch Assign"
      subTitleRoute="/batch-assign"
    >
      <Box sx={{ mb: 4 }} />
      <DragDropContext onDragEnd={handleDragEnd}>
        { (assignedGroups.length || extraGroups.length || unassignedOrders.length) > 0 && (
          <>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Technician Groups</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 1, overflowX: 'auto', pb: 2, px: 1 }}>
              {/* Assigned and Extra group cards */}
              {assignedGroups.map((group, idx) => (
                <Droppable key={`assigned-${idx}`} droppableId={`assigned-${idx}`}>
                  {(provided) => (
                    <Card
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        flex: '0 0 300px',
                        minWidth: 300,
                        maxWidth: 300,
                        backgroundColor: savedAssigned[idx]
                          ? 'rgba(0,25,80,0.8)'
                          : 'rgba(0,25,80,0.6)',
                        color: '#fff',
                      }}
                      elevation={2}
                    >
                      <IconButton
                        onClick={() => handleRemoveGroup(true, idx)}
                        sx={{ position: 'absolute', top: 8, right: 8, color: '#fff' }}
                      >
                        <CloseIcon />
                      </IconButton>
                      <CardContent
                        sx={{
                          pt: 4,
                          pb: 2,
                          flexGrow: 1,
                          overflowY: 'auto'
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          sx={{ cursor: 'pointer' }}
                          onClick={() => handleOpenEditGroup(idx)}
                        >
                          Technicians
                        </Typography>
                        <Stack spacing={1} mb={2}>
                          {group.technicians.map(t => (<Typography key={t.id} variant="body2" sx={{pl:2}}>{`${t.firstname} ${t.lastname}`}</Typography>))}
                        </Stack>
                        <Typography variant="subtitle2" fontWeight="bold">
                          Assigned Jobs
                        </Typography>
                        {group.orders.map((o, j) => (
                          <Draggable key={o.id} draggableId={o.id} index={j}>
                            {(dragProvided) => (
                              <Box
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...dragProvided.dragHandleProps}
                                sx={{ mb: 1, p: 1, backgroundColor: '#fafafa', borderRadius: 1 }}
                              >
                                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                  {`${formatTime(o.start_time)} - ${formatTime(o.end_time)}`}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                  {o.client?.name}
                                </Typography>
                                {/* Job types */}
                                <Box sx={{ mt: 0.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                  {(() => {
                                    let types = [];
                                    if (Array.isArray(o.job_types)) {
                                      types = o.job_types;
                                    } else if (typeof o.job_types === "string") {
                                      types = [o.job_types];
                                    }

                                    return types.slice(0, 2).map(type => (
                                      <Chip
                                        key={type}
                                        label={type.replace(/_/g, " ")}
                                        size="small"
                                      />
                                    ));
                                  })()}
                                </Box>

                                {/* Work permit badge — assigned variant */}
                                {o.work_permit_required && (
                                  <Box sx={{ mt: 0.5, display: "flex", justifyContent: "flex-end" }}>
                                    <Chip
                                      label="WORK PERMIT REQUIRED"
                                      size="small"
                                      variant="outlined"
                                      sx={{
                                        borderColor: "rgba(25,118,210,0.5)",
                                        color: "#1976d2",
                                        bgcolor: "rgba(25,118,210,0.06)",
                                        fontWeight: 550,
                                        height: 15,
                                        fontSize: "0.65rem",
                                        letterSpacing: "0.2px",
                                        "& .MuiChip-label": {
                                          px: 0.75,
                                        },
                                      }}
                                    />
                                  </Box>
                                )}

                                <Typography variant="body2" sx={{ color: 'text.primary', mt: 1, fontWeight: 550 }}>
                                  {o.client?.address?.city}
                                </Typography>
                              </Box>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </CardContent>

                      {dirtyAssigned[idx] && group.orders.length > 0 && (
                        <Box
                          sx={{
                            p: 1,
                            display: 'flex',
                            justifyContent: 'flex-end',
                          }}
                        >
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleSaveAssigned(idx)}
                          >
                            Save
                          </Button>
                        </Box>
                      )}
                    </Card>
                  )}
                </Droppable>
              ))}

              {extraGroups.map((group, idx) => (
                <Droppable key={`extra-${idx}`} droppableId={`extra-${idx}`}>
                  {provided => (
                    <Card
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      elevation={2}
                      sx={{
                        position: 'relative',
                        flex: '0 0 300px',
                        minWidth: 300,
                        maxWidth: 300,
                        display: 'flex',
                        flexDirection: 'column',
                        bgcolor: savedExtra[idx]
                          ? 'rgba(0,25,80,0.8)'
                          : 'rgba(80,0,0,0.6)',
                        color: '#fff',
                      }}
                    >
                      {/* ✖ icon */}
                      <IconButton
                        onClick={() => handleRemoveGroup(false, idx)}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          color: '#fff'
                        }}
                      >
                        <CloseIcon />
                      </IconButton>

                      {/* Scrollable content area */}
                      <CardContent
                        sx={{
                          pt: 4,
                          pb: 2,
                          flexGrow: 1,
                          overflowY: 'auto',
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          sx={{ cursor: 'pointer' }}
                          onClick={() => handleOpenEditExtraGroup(idx)}
                        >
                          Technicians
                        </Typography>
                        <Stack spacing={1} mb={2}>
                          {group.technicians.length > 0 ? (
                            group.technicians.map(t => (
                              <Typography key={t.id} variant="body2" sx={{ pl: 2 }}>
                                {`${t.firstname} ${t.lastname}`}
                              </Typography>
                            ))
                          ) : (
                            <Typography variant="body2" sx={{ pl: 2 }}>
                              {group.name}
                            </Typography>
                          )}
                        </Stack>
                        <Typography variant="subtitle2" fontWeight="bold">
                          Assigned Jobs
                        </Typography>
                        {group.orders.map((o, j) => (
                          <Draggable key={o.id} draggableId={o.id} index={j}>
                          {(dragProvided) => (
                            <Box
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}
                            sx={{ mb: 1, p: 1, backgroundColor: '#fafafa', borderRadius: 1 }}
                            >
                            <Typography variant="body2" sx={{ color: 'text.primary' }}>
                              {`${formatTime(o.start_time)} - ${formatTime(o.end_time)}`}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.primary' }}>
                              {o.client?.name}
                            </Typography>
                            {/* Job types */}
                            <Box sx={{ mt: 0.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                              {(() => {
                                let types = [];
                                if (Array.isArray(o.job_types)) {
                                  types = o.job_types;
                                } else if (typeof o.job_types === "string") {
                                  types = [o.job_types];
                                }

                                return types.slice(0, 2).map(type => (
                                  <Chip
                                    key={type}
                                    label={type.replace(/_/g, " ")}
                                    size="small"
                                  />
                                ));
                              })()}
                            </Box>

                            {/* Work permit badge — assigned variant */}
                            {o.work_permit_required && (
                              <Box sx={{ mt: 0.5, display: "flex", justifyContent: "flex-end" }}>
                                <Chip
                                  label="WORK PERMIT REQUIRED"
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    borderColor: "rgba(25,118,210,0.5)",
                                    color: "#1976d2",
                                    bgcolor: "rgba(25,118,210,0.06)",
                                    fontWeight: 550,
                                    height: 15,
                                    fontSize: "0.65rem",
                                    letterSpacing: "0.2px",
                                    "& .MuiChip-label": {
                                      px: 0.75,
                                    },
                                  }}
                                />
                              </Box>
                            )}

                            <Typography variant="body2" sx={{ color: 'text.primary', mt: 1, fontWeight: 550 }}>
                              {o.client?.address?.city}
                            </Typography>
                            </Box>
                          )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </CardContent>

                      {/* Footer with Save button */}
                      {dirtyExtra[idx] && group.orders.length > 0 && (
                        <Box
                          sx={{
                            p: 1,
                            display: 'flex',
                            justifyContent: 'flex-end',
                          }}
                        >
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleSaveExtra(idx)}
                          >
                            Save
                          </Button>
                        </Box>
                      )}
                    </Card>
                  )}
                </Droppable>
              ))}

              {/* Add New Group Card Silhouette */}
              <Card
                variant="outlined"
                sx={{
                  flex: '0 0 300px',
                  minWidth: 300,
                  maxWidth: 300,
                  borderStyle: 'dashed',
                  borderColor: 'text.disabled',
                  borderWidth: 2,
                  borderRadius: 2,
                  color: 'text.disabled',
                  mx: 1, my: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background-color 0.2s',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    cursor: 'pointer',
                  },
                }}
                onClick={handleOpenNewGroup}
              >
                <CardActionArea
                  sx={{
                    width: '100%',
                    height: '100%',
                    p: 0, m: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AddIcon sx={{ fontSize: 48 }} />
                  <Typography
                    variant="subtitle2"
                    fontWeight="light"
                    sx={{ mt: 1 }}
                  >
                    NEW GROUP
                  </Typography>
                </CardActionArea>
              </Card>
            </Box>

            {/* Dialog for Edit Assigned Group Techs */}
            <Dialog
              open={editGroupOpen}
              onClose={handleCloseEditGroup}
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle>Edit Technicians</DialogTitle>

              <DialogContent dividers>
                <Table size="small">
                  <TableBody>
                    {(() => {
                      // IDs in the group being edited
                      const currentIds =
                        assignedGroups[editGroupIndex]?.technicians.map(t => t.id) || [];
                      // build a set of techs used by other groups
                      const usedOther = new Set(usedTechIds);
                      currentIds.forEach(id => usedOther.delete(id));

                      return technicians
                        .filter(t => !usedOther.has(t.id))
                        .map(t => {
                          // compute notes exactly like in New Group dialog
                          const notes = [];
                          if (
                            t.competency_level?.driving_4_wheels &&
                            t.competency_level.driving_4_wheels !== "N/A"
                          ) {
                            notes.push("Driver: 4 wheels");
                          }
                          if (
                            t.competency_level?.driving_2_wheels &&
                            t.competency_level.driving_2_wheels !== "N/A"
                          ) {
                            notes.push("Driver: 2 wheels");
                          }

                          const checked = editSelectedTechIds.includes(t.id);
                          return (
                            <TableRow key={t.id}>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={checked}
                                  onChange={handleEditCheckboxChange(editGroupIndex, t.id)}
                                />
                              </TableCell>
                              <TableCell>
                                {`${t.lastname}, ${t.firstname}`}                                
                              </TableCell>
                              <TableCell>
                                {notes.length > 0 && ` ${notes.join(", ")}`}
                              </TableCell>
                            </TableRow>
                          );
                        });
                    })()}
                  </TableBody>
                </Table>
              </DialogContent>

              <DialogActions>
                <Button onClick={handleCloseEditGroup}>Cancel</Button>
                <Button
                  variant="contained"
                  disabled={!editSelectedTechIds.length}
                  onClick={handleSaveEditGroup}
                >
                  Save
                </Button>
              </DialogActions>
            </Dialog>

            {/* Dialog for Edit Extra Group Techs */}
            <Dialog
              open={editExtraGroupOpen}
              onClose={handleCloseEditExtraGroup}
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle>Edit Technicians</DialogTitle>
              <DialogContent dividers>
                <Table size="small">
                  <TableBody>
                    {(() => {
                      const currentIds =
                        extraGroups[editExtraGroupIndex]?.technicians.map(t => t.id) || [];
                      // Exclude techs used elsewhere, same as in your assigned edit
                      const usedOther = new Set(usedTechIds);
                      currentIds.forEach(id => usedOther.delete(id));

                      return technicians
                        .filter(t => !usedOther.has(t.id))
                        .map(t => {
                          const notes = [];
                          if (t.competency_level?.driving_4_wheels !== 'N/A') notes.push('Driver: 4 wheels');
                          if (t.competency_level?.driving_2_wheels !== 'N/A') notes.push('Driver: 2 wheels');
                          const checked = editExtraSelectedTechIds.includes(t.id);
                          return (
                            <TableRow key={t.id}>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={checked}
                                  onChange={handleExtraCheckboxChange(t.id)}
                                />
                              </TableCell>
                              <TableCell>{`${t.lastname}, ${t.firstname}`}</TableCell>
                              <TableCell>{notes.join(', ')}</TableCell>
                            </TableRow>
                          );
                        });
                    })()}
                  </TableBody>
                </Table>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseEditExtraGroup}>Cancel</Button>
                <Button
                  variant="contained"
                  disabled={!editExtraSelectedTechIds.length}
                  onClick={handleSaveEditExtraGroup}
                >
                  Save
                </Button>
              </DialogActions>
            </Dialog>

            {/* Alert message for any unsaved cards */}
            {hasUnsavedAssignments && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                You have unsaved technician assignments
              </Alert>
            )}


            {/* Unassigned Jobs by City */}
            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>Unassigned Jobs</Typography>
            <Box 
              sx={{ 
                display: 'flex', 
                flexWrap: 'nowrap', 
                gap: 1, 
                overflowX: 'auto', 
                pb: 2, 
                px: 1 
              }}
            >
              {/* 1a) placeholder droppable if NOTHING unassigned yet */}
              {sortedCities.length === 0 && (
                <Droppable
                  key="unassigned-placeholder"
                  droppableId="unassigned-placeholder"
                >
                  {(provided) => (
                    <Card
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{
                        flex: '0 0 300px',
                        minWidth: 300,
                        maxWidth: 300,
                        minHeight: 150,
                        borderStyle: 'dashed',
                        borderColor: 'text.disabled',
                        borderWidth: 2,
                        borderRadius: 2,
                        color: 'text.disabled',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography align="center">
                        DROP HERE TO<br/>
                        UNASSIGN TECHNICIANS
                      </Typography>
                      {provided.placeholder}
                    </Card>
                  )}
                </Droppable>
              )}
              
              {/* 1b) existing city cards */}
              {sortedCities.map(city => {
                const key = `unassigned-${city.replace(/\s+/g, '_')}`;
                return (
                  <Droppable key={city} droppableId={key}>
                    {provided => (
                      <Card
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          flex: '0 0 300px',
                          minWidth: 300,
                          maxWidth: 300,
                          bgcolor: savedUnassigned[key]
                            ? '#e0e0e0'   // slightly darker
                            : '#f0f0f0',
                        }}
                      >
                        <CardHeader title={city} />
                        <CardContent 
                          sx={{
                            p: 1,
                            pb: 2,
                            flexGrow: 1,
                            overflowY: 'auto',
                          }}
                        >
                          {cityGroups[city].map((order, idx) => (
                            <Draggable
                              key={order.id}
                              draggableId={order.id}
                              index={idx}
                            > 
                              {dragProv => (
                                <Box
                                  ref={dragProv.innerRef}
                                  {...dragProv.draggableProps}
                                  {...dragProv.dragHandleProps}
                                  sx={{
                                    mb: 1,
                                    p: 1,
                                    bgcolor: '#fff',
                                    borderRadius: 1,
                                    position: 'relative',   // REQUIRED for bottom-right anchor
                                  }}
                                >
                                  <Typography variant="body2">
                                    {formatTime(order.start_time)} – {formatTime(order.end_time)}
                                  </Typography>
                                  <Typography variant="body2">
                                    {order.client.name}
                                  </Typography>
                                  {/* Job types */}
                                  <Box sx={{ mt: 0.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                    {(() => {
                                      let types = [];
                                      if (Array.isArray(order.job_types)) {
                                        types = order.job_types;
                                      } else if (typeof order.job_types === "string") {
                                        types = [order.job_types];
                                      }

                                      return types.slice(0, 2).map(type => (
                                        <Chip
                                          key={type}
                                          label={type.replace(/_/g, " ")}
                                          size="small"
                                        />
                                      ));
                                    })()}
                                  </Box>

                                  {/* Work permit badge — always its own line */}
                                  {order.work_permit_required && (
                                    <Box sx={{ mt: 0.75, display: "flex", justifyContent: "flex-end" }}>
                                      <Chip
                                        label="WORK PERMIT REQUIRED"
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                          borderColor: "rgba(25,118,210,0.5)",
                                          color: "#1976d2",
                                          bgcolor: "rgba(25,118,210,0.06)",
                                          fontWeight: 550,
                                          height: 15,
                                          fontSize: "0.65rem",
                                          letterSpacing: "0.2px",
                                          "& .MuiChip-label": {
                                            px: 0.75,
                                          },
                                        }}
                                      />
                                    </Box>
                                  )}
                                </Box>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}

                          {dirtyUnassigned[key] && cityGroups[city].length > 0 && (
                            <Box
                              sx={{
                                p: 1,
                                display: 'flex',
                                justifyContent: 'flex-end',
                              }}
                            >
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleSaveUnassigned(city)}
                              >
                                Save
                              </Button>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </Droppable>
                );
              })}

            </Box>
          </>
        )}
      </DragDropContext>

      {/* Success snackbar */}
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ width: '100%' }}
        >
          Technician Assignments saved successfully!
        </Alert>
      </Snackbar>
    </BaseWrapper>
  );
}
